import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { buildTheme, type ThemeName } from './theme';

const STORAGE_KEY = 'opspilot-color-mode';

const THEME_NAMES: ThemeName[] = ['dark', 'light', 'cloud2-dark', 'cloud2-light'];

interface ThemeModeContextValue {
  themeName: ThemeName;
  setThemeName: (name: ThemeName) => void;
  /** Legacy toggle: cycles dark -> light -> cloud2-dark -> cloud2-light */
  toggleTheme: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  themeName: 'dark',
  setThemeName: () => {},
  toggleTheme: () => {},
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

function getStoredTheme(): ThemeName {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && THEME_NAMES.includes(stored as ThemeName)) return stored as ThemeName;
  } catch {
    // localStorage may be unavailable
  }
  return 'dark';
}

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeName, setThemeNameState] = useState<ThemeName>(getStoredTheme);

  const setThemeName = useCallback((name: ThemeName) => {
    setThemeNameState(name);
    try {
      localStorage.setItem(STORAGE_KEY, name);
    } catch {
      // ignore
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeNameState((prev) => {
      const idx = THEME_NAMES.indexOf(prev);
      const next = THEME_NAMES[(idx + 1) % THEME_NAMES.length];
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const theme = useMemo(() => buildTheme(themeName), [themeName]);

  const contextValue = useMemo(
    () => ({ themeName, setThemeName, toggleTheme }),
    [themeName, setThemeName, toggleTheme],
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
